import React, { ReactNode } from "react";
import { FaWhatsapp, FaCheckCircle } from "react-icons/fa";
import { MdConstruction, MdElectricBolt, MdPlumbing } from "react-icons/md";
import Button from "./Button";
import COMPANY from "../constants";

const logo = new URL("../assets/logo4.png", import.meta.url).href;

interface HeroProps {
  phoneNumber: string;
}

interface Feature {
  icon: ReactNode;
  text: string;
}

function Hero({ phoneNumber }: HeroProps) {
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  const features: Feature[] = [
    { icon: <FaCheckCircle />, text: "Profissional Qualificado" },
    { icon: <FaCheckCircle />, text: "Atendimento Rápido" },
    { icon: <FaCheckCircle />, text: "Serviço Com Garantia!" },
    { icon: <FaCheckCircle />, text: "Orçamento Gratuito" },
  ];

  return (
    <section
      id="home"
      className="relative pt-16 bg-gradient-to-br from-sd-dark via-gray-900 to-gray-800 text-white overflow-hidden"
    >
      {/* Background Pattern Sutil */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-20 left-10 text-8xl md:text-9xl animate-pulse">
          <MdConstruction />
        </div>
        <div className="absolute bottom-20 right-20 text-8xl md:text-9xl animate-pulse delay-700">
          <MdElectricBolt />
        </div>
        <div className="absolute top-1/2 right-10 text-7xl md:text-8xl animate-pulse delay-300">
          <MdPlumbing />
        </div>
      </div>

      {/* Gradient Orbs */}
      <div className="absolute top-0 left-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-blue-500 opacity-5 rounded-full blur-[80px] md:blur-[120px]"></div>
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] md:w-[500px] md:h-[500px] bg-purple-500 opacity-5 rounded-full blur-[80px] md:blur-[120px]"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-16 lg:py-20 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center md:text-left md:grid md:grid-cols-2 md:gap-12 lg:gap-16 md:items-center">
            
            {/* Logo Column - MUITO MENOR */}
            <div className="w-full flex justify-center items-center order-1 md:order-2 mb-4 md:mb-0 py-4 md:py-0">
              <div className="relative group w-full md:max-w-none">
                {/* Efeitos de brilho */}
                <div className="absolute inset-0 md:-inset-24 lg:-inset-32 xl:-inset-40 bg-gradient-to-r from-white/12 via-white/8 to-white/12 rounded-full blur-none md:blur-[160px] lg:blur-[180px] group-hover:blur-none md:group-hover:blur-[200px] transition-all duration-700"></div>
                <div className="absolute inset-0 md:-inset-20 lg:-inset-24 xl:-inset-32 bg-white/8 rounded-full blur-none md:blur-[120px] lg:blur-[140px] group-hover:bg-white/12 transition-all duration-500"></div>

                {/* Logo - MUITO MENOR */}
                <div className="relative">
                  <img
                    src={logo}
                    alt="SD Serviços e Manutenções - Serviços Profissionais de Eletricista, Encanador, Pedreiro e Carpinteiro"
                    className="relative w-full h-auto object-contain 
                    drop-shadow-[0_20px_40px_rgba(0,0,0,0.5)] md:drop-shadow-[0_35px_70px_rgba(0,0,0,0.5)] 
                    transform group-hover:scale-105 transition-all duration-700 ease-out
                    max-w-[250px] sm:max-w-[300px] md:max-w-[350px] lg:max-w-[400px] xl:max-w-[450px] 2xl:max-w-[500px] 
                    mx-auto"
                  />

                  {/* Círculos decorativos rotativos */}
                  <div className="absolute inset-0 border-2 md:border-4 lg:border-[5px] border-white/10 rounded-full animate-spin-slow"></div>
                  <div className="absolute inset-6 sm:inset-8 md:inset-10 lg:inset-12 border-2 md:border-3 border-white/5 rounded-full animate-spin-reverse"></div>
                </div>

                {/* Partículas flutuantes */}
                <div className="absolute top-1/4 -left-6 sm:-left-8 md:-left-10 lg:-left-12 w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 lg:w-7 lg:h-7 bg-blue-400/30 rounded-full animate-pulse"></div>
                <div className="absolute top-1/3 -right-6 sm:-right-8 md:-right-10 lg:-right-12 w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 bg-green-400/30 rounded-full animate-pulse delay-300"></div>
                <div className="absolute bottom-1/4 -left-5 sm:-left-7 md:-left-9 lg:-left-11 w-3.5 h-3.5 sm:w-4.5 sm:h-4.5 md:w-5.5 md:h-5.5 lg:w-6.5 lg:h-6.5 bg-purple-400/30 rounded-full animate-pulse delay-500"></div>
                <div className="absolute bottom-1/3 -right-5 sm:-right-7 md:-right-9 lg:-right-11 w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 bg-yellow-400/30 rounded-full animate-pulse delay-700"></div>
              </div>
            </div>

            {/* Content Column */}
            <div className="w-full max-w-xl md:max-w-none order-2 md:order-1">
              {/* Badge Disponível */}
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-2 mb-6 backdrop-blur-sm">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-400"></span>
                </span>
                <span className="text-xs sm:text-sm font-bold text-white tracking-wide">
                  DISPONÍVEL AGORA
                </span>
              </div>

              {/* Título Principal */}
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
                Serviços Profissionais de Manutenção em<span className="block mt-1">{COMPANY.location}, SC</span>
              </h1>

              {/* Subtítulo */}
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-gray-300 leading-relaxed px-4 md:px-0">
                Profissional{" "}
                <span className="text-white font-semibold">qualificado</span> e{" "}
                <span className="text-white font-semibold">experiente</span>{" "}
                para cuidar do seu lar e do seu negócio com excelência
              </p>

              {/* Features List */}
              <div className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-3 mb-6 md:mb-8 px-4 md:px-0">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-lg sm:rounded-xl px-3 sm:px-4 py-2 sm:py-2.5 hover:bg-white/15 hover:border-white/30 transition-all duration-300"
                  >
                    <span className="text-green-400 text-sm sm:text-base">
                      {feature.icon}
                    </span>
                    <span className="text-xs sm:text-sm md:text-base font-semibold whitespace-nowrap">
                      {feature.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-8 md:mb-10 px-4 md:px-0">
                <Button
                  href={`https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                    "Olá! Gostaria de solicitar um serviço."
                  )}`}
                  variant="primary"
                  className="flex items-center justify-center gap-3 group shadow-xl hover:shadow-2xl transition-all duration-300 text-base sm:text-lg py-3.5 sm:py-4 font-bold"
                >
                  <FaWhatsapp className="text-xl sm:text-2xl group-hover:scale-110 transition-transform duration-300" />
                  <span>Solicitar Serviço</span>
                </Button>
                <Button
                  onClick={() => scrollToSection("servicos")}
                  variant="secondary"
                  className="backdrop-blur-sm border-2 hover:bg-white hover:text-sd-dark transition-all duration-300 text-base sm:text-lg py-3.5 sm:py-4 font-semibold"
                >
                  Ver Serviços
                </Button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-4 sm:gap-6 md:gap-8 pt-6 md:pt-8 border-t border-white/20">
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2"></div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-gray-400"></div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2"></div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-gray-400"></div>
                </div>
                <div className="text-center">
                  <div className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-1 sm:mb-2"></div>
                  <div className="text-[10px] sm:text-xs md:text-sm text-gray-400"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg
          className="w-full h-16 sm:h-24 md:h-32 fill-white"
          viewBox="0 0 1200 120"
          preserveAspectRatio="none"
        >
          <path
            d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z"
            opacity=".25"
            className="animate-wave"
          ></path>
          <path
            d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z"
            opacity=".5"
            className="animate-wave-slow"
          ></path>
          <path
            d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z"
            className="animate-wave-fast"
          ></path>
        </svg>
      </div>

      {/* Animações Customizadas */}
      <style jsx>{`
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        @keyframes spin-reverse {
          from {
            transform: rotate(360deg);
          }
          to {
            transform: rotate(0deg);
          }
        }
        .animate-spin-slow {
          animation: spin-slow 20s linear infinite;
        }
        .animate-spin-reverse {
          animation: spin-reverse 15s linear infinite;
        }
        .delay-300 {
          animation-delay: 300ms;
        }
        .delay-500 {
          animation-delay: 500ms;
        }
        .delay-700 {
          animation-delay: 700ms;
        }
      `}</style>
    </section>
  );
}

export default Hero;